/* eslint-disable no-param-reassign */
 

const { possessive } = require('lib/text')
const g = require('globals')
const Backbone = require('lib/backbone')
const _ = require('lib/underscore')
const $ = require('lib/jquery')
const BaseModel = require('lib/base_model')
const UserRelations = require('module/user/user_relations')
const Diacritics = require('lib/diacritics')
const social_links = require('data/social_links')
const dom_json = require('lib/dom_json')
const hasFeature = require('module/user/helpers/has_feature')
const { getImgixUrl, getCloudflareUrl } = require('@aboutdotme/image-helpers')
const getActiveImage = require('module/user/helpers/get_active_image')
const getDisplayImage = require('module/user/helpers/get_display_image')
const isAllowedFeature = require('module/user/helpers/is_allowed_feature')
const fullName = require('module/user/helpers/full_name')
const displayName = require('module/user/helpers/display_name')
const displayFirstName = require('module/user/helpers/display_first_name')
const canonicalUrl = require('module/user/helpers/canonical_url')
const isUser = require('module/user/helpers/is_user')
const log = require('log').create('user')
const config = require('lib/config')

if (! window.devicePixelRatio) window.devicePixelRatio = 1


// local model and collection types for scope tracking
// used for tracking if scopes have been fetched already
const UserScope = BaseModel.extend({
    idAttribute: 'scope',
})
const UserScopes = Backbone.Collection.extend({
    model: UserScope,
})

const User = BaseModel.extend({

    relations: UserRelations,

    urlRoot() {
        return `${g.NODE_API}/user`
    },

    idAttribute: 'lowercase_user_name',

    calculated_values: {
        full_name() {
            // return first and last name
            return fullName(this.toJSON())
        },
        last_first() {
            // return last_name and first_name, useful for sorting on last_name, so first_name breaks a tie
            const last = this.get('last_name').trim()
            const first = this.get('first_name').trim()
            const last_first = `${last} ${first}`
            return last_first.trim()
        },
        display_name() {
            // return full name, or user_name as a fallback
            return displayName(this.toJSON())
        },
        display_first_name() {
            // return first name, or user_name as a fallback
            return displayFirstName(this.toJSON())
        },
        possessive_first_name() {
            const name = this.get('display_first_name')
            return possessive(name)
        },
        possessive_display_name() {
            const name = this.get('display_name')
            return possessive(name)
        },
        page_url() {
            return `${g.DOMAIN_NAME_URL}/${this.get('user_name')}`
        },
        canonical_url() {
            // returns mapped domain or about.me url
            return canonicalUrl(this.toJSON(), g.DOMAIN_NAME_URL)
        },
        domain() {
            // domains object might not exist on every user
            // cuz we don't instantiate all the defaults
            const domains = this.get('mapped_domains')
            return domains && domains.getDomain()
        },
    },

     
    constructor: function constructor(attributes, options = {}) {
        // allow for string user_name as sole argument
        // auto-fetch users created with just a username
        if (_.isString(attributes)) {
            attributes = { user_name: attributes }
            options.initial_fetch = true
        }

        // sanitize initial input before calling the constructor
        attributes = this.sanitize(attributes)

        this.scopes = new UserScopes()

        // inherit superclass constructor
        BaseModel.prototype.constructor.apply(this, [attributes, options])

        if (options.initial_fetch) this.fetch()
    },

    initialize(attributes, options) {
        // inherit superclass initialize
        BaseModel.prototype.initialize.apply(this, arguments)

        // validate
        const error = this.validate(this.attributes)
        if (error) this.trigger('error', this, error)

        this.on('change:user_name', (model, user_name) => {
            this.set('lowercase_user_name', user_name.toLowerCase())
        })

        // if the user changes their active image, deleted the cached_thumb_url
        this.on('change:images[*]', () => {
            delete this.cached_thumb_url
        })
    },

    sanitize(attributes) {
        attributes = attributes || {}
        // shim for users created with an array of [user_name, display_name]
        // this shim relies on the next shim to split the display_name for us
        if (attributes instanceof Array) {
            const arr = attributes
            attributes = {}
            attributes.user_name = String(arr[0])
            attributes.display_name = String(arr[1])
        }

        // shim for users created with display_name and no first or last name
        if (_.isString(attributes.display_name) &&
            _.isUndefined(attributes.first_name) &&
            _.isUndefined(attributes.last_name)) {

            // split the display_name on the first space, yuck
            const names = attributes.display_name.split(' ')
            attributes.first_name = names.shift()
            attributes.last_name = names.join(' ').trim()
        }

        // delete attributes.display_name
        // even if attributes.first_name and attributes.last_name are provided
        // display_name is a calculated value
        if (_.isString(attributes.display_name)) {
            delete attributes.display_name
        }

        // collection members have an array property called display
        if (_.isArray(attributes.display)) {
            attributes.first_name = attributes.display[0]
            attributes.last_name = attributes.display[1]
            delete attributes.display
        }
        // collection members have a user_name property called 'name'
        if (_.isString(attributes.name)) {
            attributes.user_name = attributes.name
            delete attributes.name
        }

        // coerce strings
        _.each(['user_name', 'first_name', 'last_name'], (key) => {
            let value = attributes[key]
            if (_.isUndefined(value) || _.isNull(value)) value = ''
            attributes[key] = String(value)
        })

        // add lowercase_user_name for use as idAttribute
        // but only if user_name is defined
        if (attributes.user_name) {
            attributes.lowercase_user_name = attributes.user_name.toLowerCase()
        }

        // log(attributes);
        return attributes
    },

    isUser(u) {
        return isUser(this.toJSON(), u)
    },

    hasFeature(feature) {
        return hasFeature(this.toJSON(), feature)
    },

    isAllowedFeature(feature) {
        return isAllowedFeature(this.toJSON(), feature)
    },

    hasApp(app_name) {
        let has_app = this.get('apps').findWhere({ platform: app_name })
        const supported_apps = _.pluck(social_links, 'id')

        if (has_app && this.get('layout.version') === 1) {
            if (! _.contains(supported_apps, app_name)) has_app = false
        }

        return !! has_app // !! forces boolean
    },

    getAlphaSortValue(keys) {
        // returns a value suitable for alphabetical sorting
        // using the first key in the list, with subsequent keys as fallbacks
        // replaces diacritics and removes any non-alpha characters at the beginning of the string
        if (! keys || ! keys.length) return ''
        const key = keys.shift()
        let value = String(this.get(key))
        value = Diacritics.remove(value)
        value = value.trim().toLowerCase().replace(/^[^a-z]+/, '')

        // if we end up with an empty value, try again with the next key
        if (! value) return this.getAlphaSortValue(keys)

        return value
    },

    getActiveImage() {
        // returns object other than model
        return getActiveImage(this.toJSON())
    },

    getImgixUrl(options = {}) {
        const has_active = this.getActiveImage()

        _.defaults(options, {
            width: 105,
            height: 70,
            quality: 80,
            IMGIX_URL: g.IMGIX_URL,
        })

        if (has_active) {
            const image = getDisplayImage(this.toJSON())
            return getImgixUrl(image, options)
        }

        return false

    },

    getCloudflareUrl(options = {}) {
        const has_active = this.getActiveImage()

        _.defaults(options, {
            width: 105,
            height: 70,
            quality: 80,
            image_domain: config.get('pumpkinhead.globals.aws_images'),
        })

        if (has_active) {
            const image = getDisplayImage(this.toJSON())
            return getCloudflareUrl(image, options)
        }

        return false

    },

    fetchThumbUrl() {
        // for fetching generated imgix thumb on our s3 bucket
        const deferred = $.Deferred()

        // caching this URL so we don't need to request it again
        // unless the user changes their image
        // (in which case this cached attribute is deleted)
        if (this.cached_thumb_url) {
            deferred.resolve(this.cached_thumb_url)
            return deferred.promise()
        }

        const thumb_url = `${g.NODE_API}/${this.get('lowercase_user_name')}/emailsignature`

        if (! this.fetch_thumb_url_xhr) {
            this.fetch_thumb_url_xhr = $.ajax({
                type: 'GET',
                url: thumb_url,
            })
                .always(() => {
                    delete this.fetch_thumb_url_xhr
                })
        }

        this.fetch_thumb_url_xhr
            .done(res => {
                if (res && res.thumb_url) {
                    this.cached_thumb_url = res.thumb_url
                    deferred.resolve(res.thumb_url)
                } else {
                    deferred.reject()
                }
            })
            .fail(err => {
                deferred.reject()
            })

        return deferred.promise()
    },

    fetch(options) {
        options = options || {}
        const model = this
        const success = options.success
        const error = options.error

        options.success = function successHandler(output) {
            log('User fetch succeeded:', output)
            if (success) success.apply(model, [output])
            model.trigger('ready')
        }
        options.error = function errorHandler(output) {
            log('User fetch failed:', output)
            if (error) error.apply(model, [output])
            model.trigger('ready')
        }

        _.defaults(options, {
            type: 'GET',
            data: {},
            scope: 'summary',
        })

        // apply scope to URL
        options.url = `${this.url()}/scope/${options.scope}`

        // delegate to super's fetch method
        const xhr = BaseModel.prototype.fetch.apply(this, [options])

        // save the scope
        xhr.done((...args) => {
            this.scopes.add({
                'scope': options.scope,
                'timestamp': _.now(),
                'arguments': args,
            })
        })

        return xhr

    },

    fetchScope(scope) {
        if (! scope || ! _.isString(scope)) throw new Error('scope must be a string')

        let deferred
        let args

        // prevent duplicate scope fetches
        const existing_scope = this.scopes.findWhere({ scope })

        if (existing_scope) {
            // scope has already been fetched
            // return a promise, resolved with the existing arguments
            log('scope has already been fetched', existing_scope)
            deferred = $.Deferred()
            args = existing_scope.get('arguments')
            deferred.resolve.apply(deferred, args)
            return deferred.promise()
        } else {
            // scope has not been fetched

            // attempt to slurp json from dom, if it exists
            const json = this.getScopeDomJson(scope)

            if (json) {
                // json found, set it on the user model and return a resolved promise
                this.set(json)
                this.scopes.add({
                    scope,
                    timestamp: _.now(),
                    arguments: [],
                })
                deferred = $.Deferred()
                deferred.resolve()
                return deferred.promise()

            } else {
                // fetch it and return the xhr deferred
                return this.fetch({
                    scope,
                })
            }
        }
    },

    getScopeDomJson(scope) {
        log('fetchScopeDomJson')

        let json
        const user_name_attribute = `[data-lowercase_user_name="${this.get('lowercase_user_name')}"]`
        const scope_attribute = `[data-scope="${scope}"]`

        const selectors = [
            'script.json.user',
            user_name_attribute,
            scope_attribute,
        ]

        try {
            json = dom_json.getJsonFromDomNode(selectors.join(''))
        } catch {
            log('json not found for this user and scope')
            log('selector', selectors.join(''))
        }

        return json
    },

    parse(output) {
        if (output.error) return false
        if (output.attributes) output = output.attributes
        return this.sanitize(output)
    },

})

module.exports = User
